import React from "react";
import Icon from "../../ui/Icon";

interface TranslationStatsDetailsProps {
  totalItems: number;
  pendingItems: number;
  translatedItems: number;
  initialLabelsToTranslate: number;
  needsReviewItems?: number;
  propertiesLabelCount: number;
  i15DLabelCount: number;
  helpTextLabelCount: number;
}

const TranslationStatsDetails: React.FC<TranslationStatsDetailsProps> = ({
  totalItems,
  pendingItems,
  translatedItems,
  initialLabelsToTranslate,
  needsReviewItems,
  propertiesLabelCount,
  i15DLabelCount,
  helpTextLabelCount,
}) => {
  const translatedFromInitial = initialLabelsToTranslate - pendingItems;
  const progressPercentage =
    initialLabelsToTranslate > 0 ? (translatedFromInitial / initialLabelsToTranslate) * 100 : 0;

  return (
    <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 shadow-lg z-30">
      <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Status Breakdown */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Icon icon="timeline" />
              Status Breakdown
            </h4>
            <div className="space-y-3">
              {[
                { label: "All Items", value: totalItems, color: "teal", icon: "database" as const },
                { label: "Translated", value: translatedItems, color: "green", icon: "check" as const },
                { label: "Pending", value: pendingItems, color: "yellow", icon: "rotate" as const },
              ].map(({ label, value, color, icon }) => (
                <div
                  key={label}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 bg-${color}-400 rounded-full`} />
                    <Icon icon={icon} />
                    <span className="text-sm text-gray-600 font-medium">{label}</span>
                  </div>
                  <span className={`text-sm font-bold bg-${color}-100 text-${color}-800 px-2 py-1 rounded-md`}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Type Breakdown */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Icon icon="desktop" />
              Content Types
            </h4>
            <div className="space-y-3">
              {[
                { label: "UI Labels", count: propertiesLabelCount, color: "blue", icon: "gear" as const },
                { label: "Database Labels", count: i15DLabelCount, color: "red", icon: "database" as const },
                { label: "Help Text", count: helpTextLabelCount, color: "purple", icon: "lightbulb" as const },
              ].map(({ label, count, color, icon }) => (
                <div
                  key={label}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 bg-${color}-400 rounded-md`} />
                    <Icon icon={icon} />
                    <span className="text-sm text-gray-600 font-medium">{label}</span>
                  </div>
                  <span className={`text-sm font-bold bg-${color}-100 text-${color}-800 px-2 py-1 rounded-md`}>
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Progress Insights */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Icon icon="view" />
              Progress Insights
            </h4>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-500">Completion Rate</span>
                  <span className="text-xs font-medium text-gray-700">{progressPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-teal-400 to-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="text-xs text-gray-500 mb-1">Remaining Items</div>
                <div className="text-lg font-bold text-gray-800">{pendingItems}</div>
              </div>

              {pendingItems == 0 && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <Icon icon="check" />
                    <span className="text-sm font-medium text-green-800">Translation Completed !</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslationStatsDetails;
